import { useEffect, useId, useRef, useState } from "react";
import { useT } from "../i18n";
import { NewTenantDialog } from "../screens/TenantForm";
import { useSession } from "../state/session";
import { Icon } from "./Icon";

/** Platform admins only: open a new customer tenant. */
function NewTenantButton() {
  const t = useT();
  const { user } = useSession();
  const [open, setOpen] = useState(false);
  if (!user?.platformAdmin) return null;
  return (
    <>
      <button type="button" className="rail-link-button" onClick={() => setOpen(true)}>
        <Icon name="plus" size={15} />
        {t("tenantForm.open")}
      </button>
      {open && <NewTenantDialog onClose={() => setOpen(false)} />}
    </>
  );
}

export function TenantSwitcher() {
  return (
    <>
      <TenantPicker />
      <NewTenantButton />
    </>
  );
}

function TenantPicker() {
  const t = useT();
  const { tenants, tenant, role, user, selectTenant } = useSession();
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const canSwitch = tenants.length > 1 || !!user?.platformAdmin;
  const roleText = user?.platformAdmin ? t("tenant.platformAdmin") : role ? t(`role.${role}`) : "";

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (!tenant) {
    return <div className="tenant-box tenant-empty">{t("tenant.none")}</div>;
  }

  const body = (
    <>
      <span className="tenant-text">
        <span className="tenant-caption">{t("tenant.label")}</span>
        <span className="tenant-name">{tenant.name}</span>
        <span className="tenant-role">{roleText}</span>
      </span>
      {canSwitch && <Icon name="chevronDown" size={16} className="tenant-chevron" />}
    </>
  );

  if (!canSwitch) return <div className="tenant-box">{body}</div>;

  const choose = (id: string) => {
    selectTenant(id);
    setOpen(false);
  };

  return (
    <div className="tenant-switcher" ref={boxRef}>
      <button
        type="button"
        className="tenant-box tenant-button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={`${t("tenant.switch")}: ${tenant.name}`}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
            requestAnimationFrame(() => document.getElementById(`${listId}-0`)?.focus());
          }
        }}
      >
        {body}
      </button>
      {open && (
        <ul
          id={listId}
          role="listbox"
          aria-label={t("tenant.listLabel")}
          className="tenant-list"
          onKeyDown={(e) => {
            const items = Array.from(e.currentTarget.querySelectorAll<HTMLLIElement>("[role=option]"));
            const idx = items.indexOf(document.activeElement as HTMLLIElement);
            if (e.key === "Escape") {
              setOpen(false);
              (boxRef.current?.querySelector("button") as HTMLButtonElement | null)?.focus();
            } else if (e.key === "ArrowDown") {
              e.preventDefault();
              items[Math.min(idx + 1, items.length - 1)]?.focus();
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              items[Math.max(idx - 1, 0)]?.focus();
            }
          }}
        >
          {tenants.map((x, i) => (
            <li
              key={x.id}
              id={`${listId}-${i}`}
              role="option"
              tabIndex={-1}
              aria-selected={x.id === tenant.id}
              className="tenant-option"
              onClick={() => choose(x.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  choose(x.id);
                }
              }}
            >
              <span>{x.name}</span>
              {x.id === tenant.id && <Icon name="check" size={16} />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
